/* eslint-disable react/no-multi-comp */
import React, {
  Component, Fragment,
} from 'react';
import PropTypes from 'prop-types';
import { advisorView } from 'Context/ViewContext';
import { FaTooltipWrapper } from 'Components/UI/Tooltip';
import { PrivateLifeInsurance as SectionKey } from 'Consts/Questionnaire';
import { section } from 'Consts/Queries';
import { QueryVal } from 'Helpers/Query';
import CircleWithCount from 'Icons/CircleWithCount';
import LabeledVerticalExpander from '../UI/LabeledVerticalExpander';
import BoolInput from '../Fields/BoolInput';
import CurrencyInput from '../Fields/CurrencyInput';
import DateInput from '../Fields/DateInput';
import FieldList from '../Fields/FieldList';
import RadioInput from '../Fields/RadioInput';
import TextInput from '../Fields/TextInput';
import TextareaInput from '../Fields/TextareaInput';
import Section from '../Section';
import SelectInput from '../Fields/SelectInput';
import {
  getClientPicklistValue, shouldMakeNotesFieldReadOnly,
} from './Helpers/ClientPicklist';

const insuranceType = [
  {
    id: 'term',
    text: 'Term',
  },
  {
    id: 'whole',
    text: 'Whole life',
  },
  {
    id: 'universal',
    text: 'Universal life',
  },
  {
    id: 'variable',
    text: 'Variable life',
  },
];

const paymentMode = [
  {
    id: 'annual',
    text: 'Annual',
  },
  {
    id: 'semi-annual',
    text: 'Semi-annual',
  },
  {
    id: 'quarterly',
    text: 'Quarterly',
  },
  {
    id: 'monthly',
    text: 'Monthly',
  },
];

class LifeInsuranceList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      lifeInsuranceCompany: '',
      lifeInsuranceInsuredClientId: '',
      lifeInsuranceType: '',
    };

    this.onChange = this.onChange.bind(this);
    this.getAccountName = this.getAccountName.bind(this);
    this.hasCoverageLength = this.hasCoverageLength.bind(this);
    this.hasType = this.hasType.bind(this);
  }

  onChange(name, value) {
    this.setState({ [name]: value });
  }

  getAccountName() {
    const {
      lifeInsuranceCompany,
      lifeInsuranceInsuredClientId,
      lifeInsuranceType,
    } = this.state;
    const { insuredClientOptions } = this.props;
    const nameList = [];
    if (lifeInsuranceInsuredClientId) nameList.push(getClientPicklistValue(insuredClientOptions, lifeInsuranceInsuredClientId));
    if (lifeInsuranceCompany) nameList.push(lifeInsuranceCompany);
    let accountName = nameList.join(' - ') || 'New policy';
    if (lifeInsuranceType) {
      const typeName = insuranceType.filter(
        type => type.id === lifeInsuranceType,
      )[0].text;
      accountName = `${accountName} (${typeName})`;
    }
    return accountName;
  }

  hasCoverageLength() {
    const { lifeInsuranceType } = this.state;
    return lifeInsuranceType === 'term';
  }

  hasType() {
    const { lifeInsuranceType } = this.state;
    return (lifeInsuranceType || '') !== '';
  }

  render() {
    const {
      getAccountName,
      hasCoverageLength,
      hasType,
      onChange,
      props: {
        id, insuredClientOptions, children: removeButton, expanded,
      },
      state: {
        lifeInsuranceInsuredClientId,
      },
    } = this;
    return (
      <LabeledVerticalExpander
        transitionSpeed={0.15}
        title={getAccountName()}
        removeButton={removeButton}
        firstElementFocusable
        expanded={expanded}
      >
        <RadioInput
          label="Insurance type"
          name={`lifeInsuranceType.${id}`}
          answerKey="lifeInsuranceType"
          options={insuranceType}
          onChange={(name, value) => onChange('lifeInsuranceType', value)}
        />
        {hasType() && (
          <Fragment>
            <TextInput
              placeholder="Insurance company"
              name={`lifeInsuranceCompany.${id}`}
              answerKey="lifeInsuranceCompany"
              onChange={(name, value) => onChange('lifeInsuranceCompany', value)
              }
            />
            {hasCoverageLength() && (
              <FaTooltipWrapper title="e.g. 30, 20, 15, 10">
                <TextInput
                  placeholder="Length of coverage"
                  name={`lifeInsuranceCoverageLength.${id}`}
                  answerKey="lifeInsuranceLength"
                />
              </FaTooltipWrapper>
            )}
            <CurrencyInput
              placeholder="Coverage amount"
              name={`lifeInsuranceCoverage.${id}`}
              answerKey="lifeInsuranceCoverage"
            />
            {!hasCoverageLength() && (
              <TextInput
                placeholder="Payment terms"
                name={`lifeInsuranceTerms.${id}`}
                answerKey="lifeInsuranceTerms"
              />
            )}
            <CurrencyInput
              placeholder="Annual premium"
              name={`lifeInsurancePremium.${id}`}
              answerKey="lifeInsurancePremium"
            />
          </Fragment>
        )}
        <RadioInput
          label="Payment mode"
          name={`lifeInsuranceMode.${id}`}
          answerKey="lifeInsuranceMode"
          options={paymentMode}
        />
        {hasType() && (
          <Fragment>
            <SelectInput
              label="Insured"
              name={`lifeInsuranceInsuredClientId.${id}`}
              options={insuredClientOptions}
              answerKey="lifeInsuranceInsuredClientId"
              onChange={(name, value) => onChange('lifeInsuranceInsuredClientId', value)}
            />
            <TextInput
              placeholder="Insured Notes"
              name={`lifeInsuranceInsuredNotes.${id}`}
              answerKey="lifeInsuranceInsuredNotes"
              onChange={(name, value) => onChange('lifeInsuranceInsuredNotes', value)}
              readOnly={shouldMakeNotesFieldReadOnly(lifeInsuranceInsuredClientId)}
            />
            {!hasCoverageLength() && (
              <CurrencyInput
                placeholder="Current cash value"
                name={`lifeInsuranceCashValue.${id}`}
                answerKey="lifeInsuranceCashValue"
              />
            )}
            <DateInput
              placeholder="Policy inception date"
              name={`lifeInsuranceInception.${id}`}
              answerKey="lifeInsuranceInception"
            />
          </Fragment>
        )}
        <BoolInput
          label="Does this policy have a beneficiary?"
          name={`lifeInsuranceHasBeneficiary.${id}`}
          answerKey="lifeInsuranceHasBeneficiary"
          checkedChildren={
            <Fragment>
              <TextInput
                placeholder="Beneficiary (primary)"
                name={`lifeInsurancePrimaryBeneficiary.${id}`}
                answerKey="lifeInsurancePrimaryBeneficiary"
              />
              <TextInput
                placeholder="Beneficiary (contingent)"
                name={`lifeInsuranceContingentBeneficiary.${id}`}
                answerKey="lifeInsuranceContingentBeneficiary"
              />
            </Fragment>
          }
        />
        <TextareaInput
          requiredView={advisorView}
          placeholder="Notes"
          name={`lifeInsuranceNotes.${id}`}
          answerKey="lifeInsuranceNotes"
        />
      </LabeledVerticalExpander>
    );
  }
}

LifeInsuranceList.propTypes = {
  id: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
  expanded: PropTypes.bool,
  insuredClientOptions: PropTypes.array.isRequired,
};

LifeInsuranceList.defaultProps = {
  expanded: false,
};

const LifeInsurance = ({ insuredClientOptions }) => {
  const [count, setCount] = React.useState(0);

  return (
    <Section
      transitionSpeed={0.25}
      height={400}
      title="Life Insurance"
      sectionKey={SectionKey}
      expanded={QueryVal(section) === SectionKey}
      CountComponent={<CircleWithCount count={count} className="expander-header-count expander-header-left" />}
    >
      <FieldList
        onCountUpdate={setCount}
        label="Policy - life"
        addLabel="Add a policy"
        keyPrefix="lifeInsurances"
        Of={LifeInsuranceList}
        childProps={{ insuredClientOptions }}
      />
    </Section>
  );
};

LifeInsurance.propTypes = {
  insuredClientOptions: PropTypes.array.isRequired,
};

export default LifeInsurance;
